import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { CampaignsService } from '../services';
import { campaignsKeys } from '../queryKeys';
import { READ_ONLY_QUERY_OPTIONS } from '../queryConfig';

/**
 * A page of the offices a promo code's rule names, for the Office List viewer.
 * Search and paging are server-side; this hook only hands them to the query.
 *
 * @param {string} campaignId
 * @param {Object} [params]
 * @param {number} [params.page=1] - 1-based page.
 * @param {string} [params.search=''] - Matches office id or name.
 */
export const usePromoCodeTargetOffices = (campaignId, { page = 1, search = '' } = {}) => {
  const { data, isError, isPending, isFetching } = useQuery({
    queryKey: campaignsKeys.promoCodeTargetOffices(campaignId, search, page),
    queryFn: () => CampaignsService.getPromoCodeTargetOffices(campaignId, { page, search }),
    enabled: Boolean(campaignId),
    ...READ_ONLY_QUERY_OPTIONS,
    // Keeps the current page on screen while the next one loads, so paging and
    // typing in the search box do not blank the list.
    placeholderData: keepPreviousData,
  });

  return {
    offices: data?.offices ?? [],
    // Nullish while unknown, never 0 — the same reading useGetInternalUsers
    // gives, so a failed request is not mistaken for an empty list.
    totalCount: data?.totalCount ?? null,
    pageSize: data?.pageSize ?? null,
    isPending,
    isFetching,
    isError,
  };
};
